import { useEffect, useMemo } from "react";
import {
  getCurrentLoyaltyTier,
  getTierColor,
  shadeTierColor,
  sortLoyaltyTiers,
  tintTierColor,
} from "../../../utils/loyalty/tiers";
import { selectGuestTiersEnabled } from "../../redux/selectors/loyaltyServiceSelectors";
import { useAppSelector } from "../useReduxHooks";

const ACCENT_VARS = {
  accent: "--loyalty-yellow",
  tint: "--loyalty-yellow-background",
  dark: "--loyalty-yellow-dark",
  deep: "--loyalty-yellow-deep",
  light: "--loyalty-yellow-light",
} as const;

export const DEFAULT_LOYALTY_TINT = "#FEF6E1";

/** Matches `--loyalty-yellow` in globals.scss, for surfaces that must stay gold. */
export const DEFAULT_LOYALTY_ACCENT = "#e9af2e";

export const useLoyaltyAccent = () => {
  const loyaltyCustomerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.data
  );
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem?.data);
  const isTiered = useAppSelector(selectGuestTiersEnabled);

  return useMemo(() => {
    if (!isTiered) {
      return {};
    }

    const currentTier = getCurrentLoyaltyTier(loyaltyCustomerInfo, tierSystem);
    if (!currentTier) {
      return {};
    }

    const accent = getTierColor(
      currentTier,
      sortLoyaltyTiers(tierSystem?.tiers)
    );

    return {
      accent,
      tint: tintTierColor(accent),
      dark: shadeTierColor(accent),
      deep: shadeTierColor(accent, 0.15),
      light: tintTierColor(accent, 0.28),
    };
  }, [isTiered, loyaltyCustomerInfo, tierSystem]);
};

export const useLoyaltyTierTheme = () => {
  const accentColors = useLoyaltyAccent();

  useEffect(() => {
    const { style } = document.documentElement;
    const entries = Object.entries(ACCENT_VARS).filter(
      ([key]) => accentColors[key as keyof typeof ACCENT_VARS] !== undefined
    );

    if (entries.length === 0) {
      return;
    }

    entries.forEach(([key, name]) =>
      style.setProperty(name, accentColors[key as keyof typeof ACCENT_VARS]!)
    );

    return () => {
      Object.values(ACCENT_VARS).forEach((name) => style.removeProperty(name));
    };
  }, [accentColors]);
};
